import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AsyncState from '../../../components/AsyncState';
import { useCurrentUser } from '../../../contexts/CurrentUserContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useApi } from '../../../hooks/useApi';
import { translations } from '../../../translations';
import { describeApiError, formatDateTime } from '../../../utils/format';
import { requestJson } from '../../../utils/lifecycle';

function LastBuild({ build }) {
  const { language } = useLanguage();
  const t = translations[language];
  if (!build) return <p className="hint">{t.noBuildYet}</p>;
  const statusText = build.status === 'success' ? t.actionLabel.build_success : t.actionLabel.build_failed;
  return <p>
    <strong>{statusText}</strong> · {build.flavorName} {build.versionName} ({build.versionCode}) · {formatDateTime(build.finishedAt, language)}{' '}
    <Link to={`/builder/${encodeURIComponent(build.sessionId)}?flavor=${encodeURIComponent(build.flavorName)}`}>{t.openBuild}</Link>
  </p>;
}

function FlavorRow({ item, children }) {
  const { language } = useLanguage();
  const t = translations[language];
  return <li className="row">
    <span className="row__main">
      <span className="row__title">{item.applicationName || item.flavorName}</span>
      <span className="row__meta">{item.flavorName} · {item.versionName} ({item.versionCode}) · {t.mappingStatusLabel}: {t.mappingLabel[item.mappingStatus] || item.mappingStatus}</span>
    </span>
    <span className="row__side">{children}</span>
  </li>;
}

export default function BuildTab({ app, onChanged }) {
  const { language } = useLanguage();
  const t = translations[language];
  const { can } = useCurrentUser();
  const flavors = useApi(`/custom-apps/${app.id}/flavors`);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState({ kind: '', text: '' });
  const items = flavors.data?.items || [];
  const linked = items.filter((item) => item.linked);
  const matching = items.filter((item) => !item.linked && item.matchesCompany);
  const others = items.filter((item) => !item.linked && !item.matchesCompany);

  const change = async (flavorName, method, done) => {
    setBusy(flavorName);
    setMessage({ kind: '', text: '' });
    try {
      await requestJson(`/custom-apps/${app.id}/flavors/${encodeURIComponent(flavorName)}`, { method });
      setMessage({ kind: 'ok', text: done });
      await Promise.all([onChanged(), flavors.refresh()]);
    } catch (error) {
      if (error.status !== 401) setMessage({ kind: 'error', text: describeApiError(error, t, error.message) });
    } finally {
      setBusy('');
    }
  };

  return <section className="card stack stack--lg">
    <div className="stack stack--sm">
      <h3>{t.lastBuild}</h3>
      <LastBuild build={app.buildSummary?.lastBuild} />
    </div>
    {message.text && <p className={message.kind === 'error' ? 'notice notice--error' : 'hint'} role={message.kind === 'error' ? 'alert' : 'status'}>{message.text}</p>}
    <AsyncState state={flavors} retry={flavors.retry}>
      <div className="stack stack--sm">
      <h3>{t.linkedFlavors}</h3>
      {linked.length === 0 ? <p className="hint">{t.noFlavorLinked}</p> : <ul className="row-list">
        {linked.map((item) => <FlavorRow key={item.flavorName} item={item}>
          {can('build:run') && <Link className="btn btn--primary btn--pill" to={`/builder?flavor=${encodeURIComponent(item.flavorName)}&customApp=${encodeURIComponent(app.id)}`}>{t.buildFlavor}</Link>}
          <button className="btn btn--pill" type="button" disabled={busy === item.flavorName} onClick={() => change(item.flavorName, 'DELETE', t.flavorUnlinked)}>{t.unlinkFlavor}</button>
        </FlavorRow>)}
      </ul>}
      </div>

      <div className="stack stack--sm">
      <h3>{t.flavorsOfCompany}</h3>
      {matching.length === 0 ? <p className="hint">{t.noMatchingFlavor}</p> : <ul className="row-list">
        {matching.map((item) => <FlavorRow key={item.flavorName} item={item}>
          <button className="btn btn--pill" type="button" disabled={busy === item.flavorName} onClick={() => change(item.flavorName, 'POST', t.flavorLinked)}>{t.linkFlavor}</button>
        </FlavorRow>)}
      </ul>}
      </div>

      {others.length > 0 && <details className="stack stack--sm">
        <summary>{t.flavorsOfOthers} ({others.length})</summary>
        <ul className="row-list">
          {others.map((item) => <FlavorRow key={item.flavorName} item={item}><span className="row__meta">{item.companyName || t.unmappedCompany}</span></FlavorRow>)}
        </ul>
      </details>}
    </AsyncState>
  </section>;
}
